import { SmilePlusIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { SimpleTooltip } from '@/shared/ui/tooltip';

import { REACTION_EMOJI, REACTION_TYPES, type ReactionSummary, type ReactionType } from './reactions';

export interface ReactionsBarProps {
  reactions: readonly ReactionSummary[];
  onToggle: (type: ReactionType) => void;
  /** Open the "who reacted" list for a reaction type. */
  onShowUsers?: (type: ReactionType) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Radix hands the focus back to the trigger once the picker's exit animation ends, which can be after the reader has
 * moved on (opened the comment editor, say) and would take it from them. Focus that is already somewhere outside the
 * picker stays there; with nothing focused (the chosen option has just unmounted) Radix's default stays, so the
 * keyboard flow still lands on the trigger.
 */
function keepFocusMovedElsewhere(event: Event) {
  const focused = document.activeElement;
  if (!focused || focused === document.body) return;
  if (event.target instanceof Node && event.target.contains(focused)) return;
  event.preventDefault();
}

/** Reaction pills with counts (mine highlighted) and an emoji picker. One reaction per user. */
export function ReactionsBar({
  reactions,
  onToggle,
  onShowUsers,
  disabled,
  size = 'md',
  className,
}: ReactionsBarProps) {
  const { t } = useTranslation('shared');
  const [pickerOpen, setPickerOpen] = useState(false);
  const visible = reactions.filter((r) => r.count > 0);

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)} data-slot="reactions-bar">
      {visible.map((reaction) => (
        <SimpleTooltip key={reaction.type} content={t(`reactions.${reaction.type}`)}>
          <button
            type="button"
            disabled={disabled}
            aria-pressed={reaction.reactedByMe}
            aria-label={t('reactions.toggle', {
              reaction: t(`reactions.${reaction.type}`),
              count: reaction.count,
            })}
            onClick={() => onToggle(reaction.type)}
            onContextMenu={(e) => {
              if (!onShowUsers) return;
              e.preventDefault();
              onShowUsers(reaction.type);
            }}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border font-medium tabular-nums focus-ring transition-[background-color,border-color,transform] select-none active:scale-95 disabled:opacity-60',
              size === 'sm' ? 'h-6 px-1.5 text-xs' : 'h-7 px-2 text-[0.8125rem]',
              reaction.reactedByMe
                ? 'border-brand/40 bg-brand-soft text-brand-text'
                : 'border-border bg-background text-muted-foreground hover:border-border-strong hover:bg-accent',
            )}
          >
            <span className={size === 'sm' ? 'text-[0.8125rem]' : 'text-sm'} aria-hidden>
              {REACTION_EMOJI[reaction.type]}
            </span>
            {reaction.count}
          </button>
        </SimpleTooltip>
      ))}
      {onShowUsers && visible.length > 0 && (
        <button
          type="button"
          onClick={() => {
            const first = visible[0];
            if (first) onShowUsers(first.type);
          }}
          className="text-xs text-muted-foreground hover:text-foreground hover:underline"
        >
          {t('reactions.whoReacted')}
        </button>
      )}
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={t('reactions.add')}
            className={cn(
              'inline-flex items-center justify-center rounded-full text-muted-foreground focus-ring transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50',
              size === 'sm' ? 'size-6' : 'size-7',
            )}
          >
            <SmilePlusIcon className="size-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="start"
          className="w-auto rounded-full p-1"
          onCloseAutoFocus={keepFocusMovedElsewhere}
        >
          <div className="flex items-center gap-0.5" role="listbox" aria-label={t('reactions.add')}>
            {REACTION_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                role="option"
                aria-selected={reactions.some((r) => r.type === type && r.reactedByMe)}
                aria-label={t(`reactions.${type}`)}
                title={t(`reactions.${type}`)}
                onClick={() => {
                  onToggle(type);
                  setPickerOpen(false);
                }}
                className="flex size-9 items-center justify-center rounded-full text-xl focus-ring transition-transform duration-150 hover:scale-125 hover:bg-accent aria-selected:bg-brand-soft"
              >
                {REACTION_EMOJI[type]}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
